import { Field, FieldProps } from 'formik';
import React, { InputHTMLAttributes, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { formatNumber } from '../../../utils/locale-utils';

export type InputProps = {
  type: string;
  controlId?: string;
  label?: string;
  className?: string;
  placeholder?: string;
  showValidation?: boolean;
  showError?: boolean;
  disabled?: boolean;
  required?: boolean;
  mapValue?: (value: string) => string;
  additionalAttributes?: {
    step?: InputHTMLAttributes<HTMLInputElement>['step'];
    min?: InputHTMLAttributes<HTMLInputElement>['min'];
    maxLength?: InputHTMLAttributes<HTMLInputElement>['maxLength'];
  };
};

export const Input: React.FC<React.PropsWithChildren<InputProps>> = ({
  type,
  controlId,
  label,
  placeholder,
  className = '',
  showValidation = true,
  showError = true,
  disabled = false,
  required = false,
  mapValue = (value) => value,
  additionalAttributes,
}) => {
  const [editingBigNumber, setEditingBigNumber] = useState(false);
  const handleBigNumberBlur = (event: any, field: any) => {
    field.onBlur(event);
    setEditingBigNumber(false);
  };
  const handleBigNumberFocus = () => {
    setEditingBigNumber(true);
  };

  const props = useMemo(() => {
    const newProps: any = {};

    if (controlId) {
      newProps.controlId = controlId;
      newProps.className = 'form-group';
    }

    return newProps;
  }, [controlId]);

  return (
    <div className={`input${className ? ` ${className}` : ''}`}>
      <Field name={controlId ?? undefined}>
        {({ field, meta }: FieldProps) => {
          const valueToMap = field.value === null || field.value === undefined ? '' : field.value;
          const mappedValue = mapValue(valueToMap);

          return (
            <Form.Group {...props}>
              {label && (
                <Form.Label className={required ? 'fw-bold' : ''}>
                  {label} {required ? ' *' : ''}
                </Form.Label>
              )}
              {type !== 'big-number' && (
                <Form.Control
                  type={type}
                  value={mapValue(field.value || '')}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={placeholder}
                  isValid={showValidation && !meta.error && meta.touched}
                  isInvalid={showValidation && !!meta.error && meta.touched}
                  disabled={disabled}
                  title={mappedValue}
                  {...additionalAttributes}
                />
              )}
              {type === 'big-number' && (
                <>
                  {editingBigNumber && (
                    <Form.Control
                      type="number"
                      autoFocus={true}
                      value={mappedValue}
                      onChange={field.onChange}
                      onBlur={(event: any) => handleBigNumberBlur(event, field)}
                      onWheel={(e: any) => e.target.blur()} // prevent modifying value when scrolling
                      placeholder={placeholder}
                      isValid={showValidation && !meta.error && meta.touched}
                      isInvalid={showValidation && !!meta.error && meta.touched}
                      disabled={disabled}
                      title={mappedValue}
                      {...additionalAttributes}
                    />
                  )}
                  {!editingBigNumber && (
                    <Form.Control
                      type="text"
                      value={formatNumber(field.value)}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={placeholder}
                      isValid={showValidation && !meta.error && meta.touched}
                      isInvalid={showValidation && !!meta.error && meta.touched}
                      disabled={disabled}
                      title={mappedValue}
                      onFocus={handleBigNumberFocus}
                      {...additionalAttributes}
                    />
                  )}
                </>
              )}
              {showError && <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>}
            </Form.Group>
          );
        }}
      </Field>
    </div>
  );
};
